import { useMemo } from 'react'

import { formatUnits } from 'viem'

import { DialogFormContent } from '~/components/forms/transfer/dialog/DialogFormContent'
import { SupportedChainId } from '~/constants/chains'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useDialogRoutePath } from '~/hooks/utils'
import { IAddress, TransactionRecipient } from '~/types'

export const useDefaultRecipientsFormatted = (
  recipient: IAddress | undefined,
) => {
  return useMemo(() => {
    return recipient ? [{ address: recipient }] : []
  }, [recipient])
}

export const DialogWrapper = ({
  defaultSender,
  defaultRecipients,
  defaultTransferToken,
  defaultMemo,
  transactionId,
  setIsOpen,
}: {
  defaultSender?: IAddress
  defaultRecipients?: TransactionRecipient[]
  defaultTransferToken?: {
    token?: {
      address: IAddress
      chainId: SupportedChainId
    }
  }
  defaultMemo?: string
  transactionId?: string
  setIsOpen: (isOpen: boolean) => void
}) => {
  useDialogRoutePath({
    closeModal: () => setIsOpen(false),
    globalModal: false,
    getPath: () => {
      if (transactionId && defaultSender) {
        return `/accounts/${defaultSender}/transactions/${transactionId}/`
      }
    },
  })

  const tokenMetadata = useTokenMetadata({
    address: defaultTransferToken?.token?.address,
    chainId: defaultTransferToken?.token?.chainId,
  })

  const defaultRecipientsFormatted = useMemo(() => {
    if (!tokenMetadata) return []
    return defaultRecipients?.map((recipient) => ({
      address: recipient.address,
      amount: formatUnits(BigInt(recipient.amount), tokenMetadata.decimals),
      compliantPaymentRelationId: recipient.compliantPaymentRelationId,
    }))
  }, [defaultRecipients, tokenMetadata])

  const defaultTokenFormatted = useMemo(() => {
    if (!tokenMetadata) return undefined
    return {
      token: defaultTransferToken?.token?.address,
      chainId: defaultTransferToken?.token?.chainId,
      amount: formatUnits(
        defaultRecipients?.reduce((acc, recipient) => {
          return acc + BigInt(recipient.amount)
        }, BigInt(0)) ?? BigInt(0),
        tokenMetadata.decimals,
      ),
    }
  }, [defaultRecipients, defaultTransferToken, tokenMetadata])

  if (!tokenMetadata?.validToken) return null

  return (
    <DialogFormContent
      setIsOpen={setIsOpen}
      defaultSender={defaultSender}
      defaultMemo={defaultMemo}
      transactionId={transactionId}
      defaultRecipients={defaultRecipientsFormatted}
      defaultToken={defaultTokenFormatted}
    />
  )
}
